import { NgClass } from '@angular/common';
import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    computed,
    forwardRef,
    input,
    model,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { InputSelectWrapperComponent } from '../input-select-wrapper/input-select-wrapper.component';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';
import { M020C060InputCheckboxComponent } from '../m020-c060-input-checkbox/m020-c060-input-checkbox.component';

type DropdownValue = string[] | undefined;

@Component({
    standalone: true,
    imports: [NgClass, M020C060InputCheckboxComponent, InputWrapperComponent, InputSelectWrapperComponent],
    selector: './ui-input-dropdown',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    templateUrl: './m020-c100-input-dropdown.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputDropdownComponent), multi: true }],
})
export class InputDropdownComponent implements ControlValueAccessor {
    options = input.required<Array<{ value: string; label: string }>>();
    label = input<string>();
    dropdownAsOverlay = input(true, { transform: booleanAttribute });

    placeholder = input<string>();

    open = signal(false);

    selected = model<DropdownValue>();

    selectedLength = computed(() => {
        return this.selected()?.length ?? 0;
    });

    disabled = model(false);

    onChange?: (value: DropdownValue) => void;
    onTouch?: () => void;

    writeValue(value: string[]) {
        this.selected.set(value);
    }

    registerOnChange(fn: (value: DropdownValue) => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouch = fn;
    }

    setDisabledState?(isDisabled: boolean) {
        this.disabled.set(isDisabled);
    }

    addOrRemoveKey(key: string, add: boolean) {
        if (this.disabled()) return;

        const selected = this.selected() ?? [];

        if (add) {
            this.selected.set([...selected, key]);
        } else {
            const findKey = selected.indexOf(key);
            if (findKey !== -1) {
                const newSelected = [...selected];
                newSelected.splice(findKey, 1);
                this.selected.set(newSelected.length > 0 ? newSelected : undefined);
            }
        }

        this.onChange?.(this.selected());
    }

    openDropdown() {
        this.open.set(!this.open());
        this.onTouch?.();
    }
}
